import Raven from 'raven-js';
// @ts-expect-error build-time virtual module
import { bender } from 'legacy-hubspot-bender-context';
import { fetchGraniteRemote, loadRemoteDlbs, loadRemoteModule, registerRemoteModules } from '../internal/module-federation/runtime';
import { findMostSpecificRoute } from 'quartz-routing/internal/routeMatchingUtils';
import { bindLinkRewriter } from './linkRewriter';
import { getAuthResult } from 'quartz-auth/service';
import { getI18nProvider } from 'quartz-i18n/service';
import { setCopilotLayoutType } from 'quartz/framework/copilot/service';
import { updateGridClasses } from '../internal/grid';
import { reconfigure } from 'raven-hubspot/configure';
import PortalIdParser from 'PortalIdParser';
import { HistoryManager } from './HistoryManager';
import { RemoteCssManager } from './RemoteCssManager';
import { setup } from '../internal/module-federation/init';
import { primaryTrackers } from 'usage-tracker-container/primaryTrackerState';
import { getCurrentPackageVersion } from 'quartz/framework/environment/service';
function metricsSafePathname() {
  const pathname = window.location.pathname;
  const portalId = PortalIdParser.get();
  if (portalId) {
    const endOfStringMatcher = new RegExp(`/${portalId}$`);
    return pathname.replace(`/${portalId}/`, `/:portalId/`).replace(endOfStringMatcher, '/:portalId');
  }
  return pathname;
}
export class GraniteRuntime {
  constructor(cssManager, historyManager) {
    this._activeApp = null;
    this._transitionContext = null;
    this._preparedCache = new Map();
    this._pendingTransition = null;
    this._localRouteOverrides = {};
    this._cssManager = cssManager !== null && cssManager !== void 0 ? cssManager : new RemoteCssManager();
    this._historyManager = historyManager !== null && historyManager !== void 0 ? historyManager : new HistoryManager();
  }
  get activeApp() {
    var _this$_activeApp$appN, _this$_activeApp;
    return (_this$_activeApp$appN = (_this$_activeApp = this._activeApp) === null || _this$_activeApp === void 0 ? void 0 : _this$_activeApp.appName) !== null && _this$_activeApp$appN !== void 0 ? _this$_activeApp$appN : null;
  }
  get transitionContext() {
    return this._transitionContext;
  }
  get cssManager() {
    return this._cssManager;
  }
  get historyManager() {
    return this._historyManager;
  }
  initialize(remoteModules, localRouteOverrides) {
    setup();
    registerRemoteModules(remoteModules);
    this._localRouteOverrides = localRouteOverrides;
  }
  getRemoteForPath(pathname) {
    var _this$_localRouteOver;
    const matchedKey = findMostSpecificRoute(Object.keys(this._localRouteOverrides), pathname);
    if (!matchedKey) return null;
    return (_this$_localRouteOver = this._localRouteOverrides[matchedKey].remoteModule) !== null && _this$_localRouteOver !== void 0 ? _this$_localRouteOver : null;
  }
  startListening(history, getContainer, callbacks) {
    const unbindLinks = bindLinkRewriter(history, this._localRouteOverrides);
    const unbindPopstate = this._historyManager.setupPopstateHandling(this._localRouteOverrides);
    const initialRemote = this.getRemoteForPath(history.location.pathname);
    if (initialRemote) {
      const container = getContainer();
      if (container) {
        this.transition(initialRemote, container).then(() => callbacks.onAppActive(initialRemote)).catch(e => {
          this._reportError(e, initialRemote);
          callbacks.onError(e);
        });
      }
    }
    const unlistenHistory = history.listen(location => {
      const remote = this.getRemoteForPath(location.pathname);
      if (remote) {
        var _this$_activeApp2;
        if (remote !== ((_this$_activeApp2 = this._activeApp) === null || _this$_activeApp2 === void 0 ? void 0 : _this$_activeApp2.appName)) {
          const container = getContainer();
          if (container) {
            callbacks.onError(null);
            callbacks.onAppActive(remote);
            this.transition(remote, container).catch(e => {
              this._reportError(e, remote);
              callbacks.onError(e);
            });
          }
        } else {
          callbacks.onAppActive(remote);
        }
      } else {
        if (this._activeApp) {
          this.deactivateActiveApp();
        }
        callbacks.onError(null);
        callbacks.onLocalActive();
      }
    });
    return () => {
      unlistenHistory();
      unbindLinks();
      unbindPopstate();
      this.deactivateActiveApp();
    };
  }
  captureTransitionContext() {
    if (!this._activeApp) {
      return undefined;
    }
    return {
      timestamp: performance.now(),
      fromPathname: metricsSafePathname(),
      fromRoute: metricsSafePathname(),
      fromRemoteApp: this._activeApp.appName
    };
  }
  activateLocal(appName, historyBasename, options) {
    const softNavigationContext = this.captureTransitionContext();
    this._activeApp = {
      appName,
      unmount: () => {}
    };
    const {
      history,
      basename
    } = this._historyManager.createLocalNestedHistory(historyBasename);
    this._historyManager.connect(appName, history, basename);
    primaryTrackers.clear();
    reconfigure({
      project: appName,
      release: getCurrentPackageVersion()
    });
    if (options.copilotLayoutType !== undefined) {
      setCopilotLayoutType(options.copilotLayoutType);
      updateGridClasses(options.copilotLayoutType);
    }
    const appResult = Promise.all([options.initApp(), this._cssManager.activate(appName)]).then(([result]) => result);
    return {
      history,
      basename,
      softNavigationContext,
      appResult
    };
  }
  async prepare(remoteName) {
    if (this._preparedCache.has(remoteName)) {
      return this._preparedCache.get(remoteName);
    }
    reconfigure({
      project: remoteName
    });
    const dlbsPromise = loadRemoteDlbs(remoteName);
    const remoteContainerPromise = fetchGraniteRemote(remoteName);
    const langPromise = loadRemoteModule(remoteName, 'lang').then(mod => mod.LANG);
    const [, remoteContainer, translations] = await Promise.all([dlbsPromise, remoteContainerPromise, langPromise]);
    const remoteModule = remoteContainer.loadEntry();
    if (!remoteModule) {
      throw new Error(`Failed to load granite remote app module "${remoteName}". Module undefined.`);
    }
    const prepared = {
      remoteName,
      remoteModule,
      translations
    };
    this._preparedCache.set(remoteName, prepared);
    return prepared;
  }
  async activate(prepared, container, softNavigationContext) {
    const {
      remoteName,
      remoteModule,
      translations
    } = prepared;
    const {
      copilotLayoutType
    } = remoteModule;
    if (copilotLayoutType !== undefined) {
      setCopilotLayoutType(copilotLayoutType);
      updateGridClasses(copilotLayoutType);
    }
    const reactRoot = await remoteModule.render({
      softNavigationContext,
      container,
      authResult: getAuthResult(),
      translations,
      localeInfo: getI18nProvider().localeInfo,
      attachHistory: this._historyManager.createAttachHistory(remoteName)
    });
    await this._cssManager.activate(remoteName);
    const handle = {
      appName: remoteName,
      unmount: () => reactRoot === null || reactRoot === void 0 ? void 0 : reactRoot.unmount()
    };
    this._activeApp = handle;
    return handle;
  }
  deactivateActiveApp() {
    if (!this._activeApp) return;
    this._transitionContext = {
      fromRemote: this._activeApp.appName,
      fromPathname: metricsSafePathname(),
      timestamp: performance.now()
    };
    this._activeApp.unmount();
    this._historyManager.disconnect();
    this._cssManager.deactivate(this._activeApp.appName);
    this._activeApp = null;
  }
  cleanup() {
    this._preparedCache.clear();
  }
  async transition(remoteName, container) {
    var _this$_activeApp3, _this$_activeApp$appN2, _this$_activeApp4, _this$_activeApp5;
    if (((_this$_activeApp3 = this._activeApp) === null || _this$_activeApp3 === void 0 ? void 0 : _this$_activeApp3.appName) === remoteName) return;
    if (this._pendingTransition) {
      this._pendingTransition.cancelled = true;
    }
    const pending = {
      remoteName,
      cancelled: false
    };
    this._pendingTransition = pending;

    // Capture from-state before any async work
    const softNavigationContext = this.captureTransitionContext();
    this._transitionContext = {
      fromRemote: (_this$_activeApp$appN2 = (_this$_activeApp4 = this._activeApp) === null || _this$_activeApp4 === void 0 ? void 0 : _this$_activeApp4.appName) !== null && _this$_activeApp$appN2 !== void 0 ? _this$_activeApp$appN2 : null,
      fromPathname: metricsSafePathname(),
      timestamp: performance.now()
    };

    // Deactivate immediately so the old app doesn't linger while the new one loads.
    (_this$_activeApp5 = this._activeApp) === null || _this$_activeApp5 === void 0 || _this$_activeApp5.unmount();
    this._activeApp = null;
    const prepared = await this.prepare(remoteName);
    if (pending.cancelled) return;
    await this.activate(prepared, container, softNavigationContext);
    this._pendingTransition = null;
  }
  preload(remoteName) {
    this.prepare(remoteName).catch(() => {});
  }
  resetForTesting() {
    this.deactivateActiveApp();
    this.cleanup();
    this._transitionContext = null;
    this._localRouteOverrides = {};
    this._cssManager.resetForTesting();
  }
  _reportError(error, remoteModule) {
    Raven.captureException(error, {
      tags: {
        project: remoteModule
      },
      extra: {
        hostProject: bender.currentProject,
        hostRelease: bender.currentProjectVersion
      }
    });
  }
}
let runtimeInstance = null;
export function getGraniteRuntime() {
  if (!runtimeInstance) {
    runtimeInstance = new GraniteRuntime();
  }
  return runtimeInstance;
}
export function resetGraniteRuntimeForTesting() {
  if (runtimeInstance) {
    runtimeInstance.resetForTesting();
  }
  runtimeInstance = null;
}